import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  CircularProgress,
  Paper,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Tooltip,
} from '@mui/material';
import { Download, Description, ExpandMore, Delete } from '@mui/icons-material';
import { API_URL } from '../../utils/config';
import {useToast} from '../../components/ToastProvider/ToastProvider';

const OrderDocumentsList = ({ orderNumber }) => {
  const [documentsByFolder, setDocumentsByFolder] = useState({
    'Shipping Documents': [],
    'Invoice Documents': [],
    'Other Documents': []
  });
  const [loading, setLoading] = useState(true);
  const [deletingFiles, setDeletingFiles] = useState(new Set());
  const { showToast } = useToast();
  
  // Check if user is admin
  const isAdmin = localStorage.getItem('isAdmin') === 'true';

  useEffect(() => {
    if (orderNumber) {
      fetchDocuments();
    }
  }, [orderNumber]);

  const fetchDocuments = async () => {
    try {
      setLoading(true);

      const token = localStorage.getItem('accessToken');
      const response = await fetch(`${API_URL}/orders/${orderNumber}/documents`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to fetch documents');
      }

      const data = await response.json();
      // Update state with documents organized by folder
      if (data.documents_by_folder) {
        setDocumentsByFolder(data.documents_by_folder);
      }
    } catch (err) {
      console.error('Error fetching documents:', err);
      showToast(`Error: ${err.message}`, 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async (fileId, fileName) => {
    try {
      const token = localStorage.getItem('accessToken');
      const response = await fetch(`${API_URL}/orders/${orderNumber}/documents/${fileId}/download`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error('Download failed');
      }

      // Create blob and download
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      showToast(`"${fileName}" downloaded successfully`, 'success'); 
    } catch (err) {
      console.error('Error downloading file:', err);
      showToast('Failed to download file', 'error');
    }
  };

  const handleDelete = async (fileId, fileName) => {
    if (!window.confirm(`Are you sure you want to delete "${fileName}"?`)) {
      return;
    }

    try {
      setDeletingFiles(prev => new Set(prev).add(fileId));
      const token = localStorage.getItem('accessToken');
      const response = await fetch(`${API_URL}/orders/${orderNumber}/documents/${fileId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Delete failed');
      }

      showToast(`"${fileName}" deleted successfully`, 'success');
      // Refresh documents list
      await fetchDocuments();
    } catch (err) {
      console.error('Error deleting file:', err);
      showToast(`Failed to delete file: ${err.message}`, 'error');
    } finally {
      setDeletingFiles(prev => {
        const newSet = new Set(prev);
        newSet.delete(fileId);
        return newSet;
      });
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Unknown';
    return new Date(dateString).toLocaleDateString();
  };

  const renderFileList = (files) => {
    if (files.length === 0) {
      return (
        <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
          No documents available in this folder.
        </Typography>
      );
    }

    return (
      <List>
        {files.map((doc, index) => (
          <ListItem key={doc.id || index} divider>
            <Description sx={{ mr: 2, color: 'primary.main' }} />
            <ListItemText
              primary={doc.name}
              secondary={
                <Box>
                  <Typography variant="caption" display="block">
                    Size: {formatFileSize(doc.size)} | Modified: {formatDate(doc.modified)}
                  </Typography>
                </Box>
              }
            />
            <ListItemSecondaryAction>
              <Tooltip title="Download">
                <IconButton
                  edge="end"
                  onClick={() => handleDownload(doc.id, doc.name)}
                  color="primary"
                  size="small"
                  sx={{ mr: 1 }}
                >
                  <Download />
                </IconButton>
              </Tooltip>
              {isAdmin && (
                <Tooltip title="Delete">
                  <IconButton
                    edge="end"
                    onClick={() => handleDelete(doc.id, doc.name)}
                    color="error"
                    size="small"
                    disabled={deletingFiles.has(doc.id)}
                  >
                    <Delete />
                  </IconButton>
                </Tooltip>
              )}
            </ListItemSecondaryAction>
          </ListItem>
        ))}
      </List>
    );
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" p={3}>
        <CircularProgress />
        <Typography variant="body2" sx={{ ml: 2 }}>
          Loading documents...
        </Typography>
      </Box>
    );
  }

  const totalFiles = Object.values(documentsByFolder).reduce((sum, files) => sum + files.length, 0);

  return (
    <Paper sx={{ p: 2, mt: 2 }}>
      <Typography variant="h6" gutterBottom>
        Order Documents
      </Typography>

      {totalFiles === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
          No documents available for this order.
        </Typography>
      ) : (
        <>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {totalFiles} document{totalFiles !== 1 ? 's' : ''} available
          </Typography>

          {/* Shipping Documents */}
          <Accordion>
            <AccordionSummary expandIcon={<ExpandMore />}>
              <Typography variant="subtitle1" fontWeight={600}>
                Shipping Documents ({documentsByFolder['Shipping Documents']?.length || 0})
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              {renderFileList(documentsByFolder['Shipping Documents'] || [])}
            </AccordionDetails>
          </Accordion>

          {/* Invoice Documents */}
          <Accordion>
            <AccordionSummary expandIcon={<ExpandMore />}>
              <Typography variant="subtitle1" fontWeight={600}>
                Invoice Documents ({documentsByFolder['Invoice Documents']?.length || 0})
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              {renderFileList(documentsByFolder['Invoice Documents'] || [])}
            </AccordionDetails>
          </Accordion>

          {/* Other Documents */}
          <Accordion>
            <AccordionSummary expandIcon={<ExpandMore />}>
              <Typography variant="subtitle1" fontWeight={600}>
                Other Documents ({documentsByFolder['Other Documents']?.length || 0})
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              {renderFileList(documentsByFolder['Other Documents'] || [])}
            </AccordionDetails>
          </Accordion>
        </>
      )}
    </Paper>
  );
};

export default OrderDocumentsList;
